import RevxFlexLayout from 'components/RevxFlexLayout';
import React, { useState } from 'react';
import { Col, Row } from 'antd';
import { JobItem } from 'domains/workspaceDomain';
import {
  ShippingBillingCustomerItem,
  WorkspaceTabShippingBillingDTO,
} from 'domains/workspaceDetailDomain';
import { addressLine } from './ShippingBillingProducer';
import cs from 'classnames';
import { RevxMoney } from 'components/RevxMoney';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { useMount } from 'ahooks';
import { addressTranslator } from 'utils/address';
import { RevxDate } from 'components/RevxDate';
import useJobCardInfo from '../../../../../hooks/useJobCardInfo';
import { RevxTrackingInfo } from 'components/RevxTrackingInfo';
import { DEFAULT_PLACEHOLDER } from 'utils/constants';
import InvoiceFiles from './InvoiceFiles';
import CostDistributionDisplay from './CostDistributionDisplay';

type Props = {
  jobData: JobItem;
  data?: WorkspaceTabShippingBillingDTO;
  hidePrice?: boolean;
};
const ShippingBillingCustomer = ({ jobData, data, hidePrice }: Props) => {
  const [shippingShow, setShippingShow] = useState(true);
  const [columns, setColumns] = useState<
    TableColumnsType<ShippingBillingCustomerItem>
  >([]);
  const { onlyOneParticipant, hasCustomer, hasProxy } = useJobCardInfo(jobData);
  useMount(() => {
    const filterIndex = ['Shipping Account', 'Arrival Date'];
    const needFilter = onlyOneParticipant && (hasCustomer || hasProxy);
    const widthFactor = needFilter ? 1.5 : 1;
    let columns: TableColumnsType<ShippingBillingCustomerItem> = [
      {
        title: 'Address',
        dataIndex: 'address',
        render: (v, r) => {
          return addressTranslator.dataToAddress(r.shippingInfo);
        },
      },
      {
        title: 'Quantity',
        dataIndex: 'itemQuantity',
        width: 100 * widthFactor,
      },
      {
        title: 'Shipping Account',
        width: 150 * widthFactor,
        dataIndex: ['shippingInfo', 'accountType'],
      },
      {
        title: 'Shipping Method',
        width: 150 * widthFactor,
        dataIndex: ['shippingInfo', 'carrierService'],
      },
      {
        title: 'Arrival Date',
        dataIndex: 'deliveryDate',
        width: 100 * widthFactor,
        render: (v) => {
          return <RevxDate dateStr={v} />;
        },
      },
      {
        title: 'Requested Delivery Date',
        dataIndex: 'expectedDeliveryDate',
        width: 150 * widthFactor,
        render: (v) => {
          return <RevxDate dateStr={v} />;
        },
      },
      {
        title: 'Price',
        dataIndex: 'price',
        width: 100 * widthFactor,
        render: (v, r) => {
          return (
            <span>
              <RevxMoney value={v} />
              {r.billedSeparately && <span>(billed separately)</span>}
            </span>
          );
        },
      },
      {
        title: 'Tracking ID',
        dataIndex: ['shippingInfo', 'trackingCode'],
        width: 100 * widthFactor,
        render: (v, r) => {
          return v ? (
            <RevxTrackingInfo
              carrierType={r.shippingInfo.carrierType}
              value={v}
            />
          ) : (
            DEFAULT_PLACEHOLDER
          );
        },
      },
    ];
    if (needFilter) {
      columns = columns.filter((c) => !filterIndex.includes(c.title as string));
    }
    setColumns(columns);
  });
  const visibleColumns = hidePrice
    ? columns.filter((column) => column.title !== 'Price')
    : columns;
  if (!data?.customerBilling) return null;
  const {
    customerShipping: { items, readyDate },
    customerBilling: {
      billingInfo: {
        billingContact,
        billtoCostCenter,
        purchaseOrder,
        accountingLine,
      },
      billingRequestor,
    },
  } = data;

  const defaultResolver = (data: string) => {
    return data || 'None';
  };

  return (
    <div>
      <InvoiceFiles jobData={jobData} data={data} />
      <div className="billing-info">
        <RevxFlexLayout className="title">Billing</RevxFlexLayout>
        <Row>
          <Col>
            <div className="col-container col-container-title mt-10">
              <span className="bold">Requestor: </span>
              <span>
                {[billingRequestor.firstName, billingRequestor.lastName]
                  .filter(Boolean)
                  .join(' ')}
                , {addressLine(billingRequestor)}
              </span>
            </div>
          </Col>
        </Row>
        <Row>
          <Col span={6}>
            <div className="col-container bold">Billing Contact</div>
            <div className="col-container">{billingContact}</div>
          </Col>
          <Col span={6}>
            <div className="col-container bold">PO No</div>
            <div className="col-container">
              {defaultResolver(purchaseOrder)}
            </div>
          </Col>
          <Col span={6}>
            <div className="col-container bold">Cost Center</div>
            <div className="col-container">
              {defaultResolver(billtoCostCenter)}
            </div>
          </Col>
          <Col span={6}>
            <div className="col-container bold">Accounting Line</div>
            <div className="col-container">
              {defaultResolver(accountingLine)}
            </div>
          </Col>
        </Row>
      </div>
      <div className="shipping-info mt-20">
        <div
          className={cs('title click', {
            'hide-title': !shippingShow,
          })}
          onClick={() => setShippingShow(!shippingShow)}
        >
          <i className="allow" />
          <span>Shipping</span>
        </div>
        <div className="shipping-content">
          <div hidden={!shippingShow}>
            <div className="shipping-title">
              <span className="bold">Ready date / ship date: </span>
              <span>
                <RevxDate dateStr={readyDate} isShippingDate={true} />
              </span>
            </div>
            <div className="shipping-table">
              <RevxTable
                className="no-border"
                dataSource={items}
                columns={visibleColumns}
                pagination={false}
                bordered={false}
              />
            </div>
          </div>
        </div>
      </div>
      <CostDistributionDisplay
        data={data.costDistribution}
        hidePrice={hidePrice}
      />
    </div>
  );
};
export default ShippingBillingCustomer;
